import { useCallback } from 'react';
import { useAuthContext } from '../../contexts/AuthContext';
import axiosInstance from '../../utils/axios';
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { useAuth } from '../../contexts/auth/useAuth';
import { SessionExpiredError } from '../../contexts/auth/authApi';

const BASE_URL =
  import.meta.env.REACT_APP_API_URL || 'https://api.jstscifree.com';

export function useApi() {
  const { account } = useAuthContext();
  const { getAccessToken } = useAuth();

  // A null token means the session is gone; the AuthGuard then sends the user
  // to /welcome.
  const getToken = useCallback(async (): Promise<string | null> => {
    try {
      return await getAccessToken();
    } catch (tokenError: unknown) {
      if (tokenError instanceof SessionExpiredError) {
        return null;
      }
      throw tokenError;
    }
  }, [getAccessToken]);

  const makeApiRequest = useCallback(
    async <T extends object | FormData>(
      url: string,
      method: AxiosRequestConfig['method'],
      data: object | null = null
    ): Promise<T> => {
      const token = await getToken();
      if (token === null) return undefined as unknown as T;

      try {
        // Check if data is FormData
        const isFormData = data instanceof FormData;

        // Add account as parameter based on request type
        let finalUrl = `${BASE_URL}${url}`;
        let finalData = data;

        if (method === 'GET' || method === 'DELETE') {
          // For GET/DELETE requests, add account as query parameter
          const separator = url.includes('?') ? '&' : '?';
          finalUrl = `${finalUrl}${separator}account=${encodeURIComponent(account)}`;
        } else if (
          method === 'POST' ||
          method === 'PUT' ||
          method === 'PATCH'
        ) {
          // For POST/PUT/PATCH requests, add account to the body
          if (finalData instanceof FormData) {
            // For FormData, append the account field
            finalData.append('account', account);
          } else {
            // For JSON data, add account to the object
            finalData = { ...data, account };
          }
        }

        const config: AxiosRequestConfig = {
          method,
          url: finalUrl,
          headers: {
            Authorization: `Bearer ${token}`,
            // Don't set Content-Type for FormData - let browser set it with boundary
            ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
          },
          data: finalData,
        };

        const response: AxiosResponse = await axiosInstance.request(config);
        return response.data;
      } catch (error: unknown) {
        // Handle axios errors
        if (axios.isAxiosError(error)) {
          if (error.response) {
            // The request was made and the server responded with error status
            throw error.response.data || error.message;
          } else if (error.request) {
            // The request was made but no response was received
            throw new Error('No response from server');
          } else {
            // Something happened in setting up the request
            throw new Error(error.message || 'Request setup failed');
          }
        }
        // If it's not an axios error, rethrow the original error
        throw error;
      }
    },
    [account, getToken]
  );

  const downloadFile = useCallback(
    async (url: string, filename: string): Promise<void> => {
      const token = await getToken();
      if (token === null) return;

      const separator = url.includes('?') ? '&' : '?';
      const finalUrl = `${BASE_URL}${url}${separator}account=${encodeURIComponent(account)}`;

      const response: AxiosResponse = await axiosInstance.request({
        method: 'GET',
        url: finalUrl,
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });

      const objectUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = objectUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
    },
    [account, getToken]
  );

  return { makeApiRequest, downloadFile };
}
